import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Typography,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { alpha } from '@mui/material/styles';
import UserDashboard from 'layouts/UserDashboard';
import Container from 'components/Container';
import { acceptHouseholdInvite } from './FinancialService';

const C = {
  ink: '#17263d',
  muted: '#78869a',
  blue: '#1475d1',
  green: '#159b72',
};

const AcceptInvitePage = (): JSX.Element => {
  const { token } = useParams<{ token: string }>();
  const navigate = useNavigate();

  const [status, setStatus] = useState<'loading' | 'success' | 'error'>(
    'loading',
  );
  const [error, setError] = useState('');

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setError('Invalid or missing invite token.');
      return;
    }
    acceptHouseholdInvite(token)
      .then(() => setStatus('success'))
      .catch(() => {
        setStatus('error');
        setError(
          'Could not accept invite. The link may have expired or already been used.',
        );
      });
  }, [token]);

  return (
    <UserDashboard>
      <Box
        sx={{
          background: `linear-gradient(175deg, ${alpha(C.blue, 0.05)} 0%, transparent 40%)`,
          minHeight: '100vh',
          py: 8,
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'center',
        }}
      >
        <Container>
          <Box sx={{ maxWidth: 480, mx: 'auto' }}>
            <Card>
              <CardContent sx={{ p: 4, textAlign: 'center' }}>
                {status === 'loading' && (
                  <>
                    <CircularProgress sx={{ mb: 2 }} />
                    <Typography variant="body1" sx={{ color: C.muted }}>
                      Accepting invite…
                    </Typography>
                  </>
                )}

                {status === 'success' && (
                  <>
                    <CheckCircleOutlineIcon
                      sx={{ fontSize: 56, color: C.green, mb: 2 }}
                    />
                    <Typography
                      variant="h5"
                      fontWeight={700}
                      sx={{ color: C.ink, mb: 1 }}
                    >
                      You've joined the household!
                    </Typography>
                    <Typography variant="body2" sx={{ color: C.muted, mb: 3 }}>
                      You can now view shared household data and manage your own
                      sharing preferences.
                    </Typography>
                    <Button
                      variant="contained"
                      onClick={() => navigate('/user/household')}
                    >
                      Go to Household
                    </Button>
                  </>
                )}

                {status === 'error' && (
                  <>
                    <Alert severity="error" sx={{ mb: 3, textAlign: 'left' }}>
                      {error}
                    </Alert>
                    <Button
                      variant="outlined"
                      onClick={() => navigate('/user/home')}
                    >
                      Go to Home
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </Box>
        </Container>
      </Box>
    </UserDashboard>
  );
};

export default AcceptInvitePage;
